'use client'
import { useMemo } from 'react'
import ErrorBoundary from '@error-boundary'
import clsx from 'clsx'
import dynamic from 'next/dynamic'

import { RaImage, RaTypography } from '@components/core/index.core'
import { useUi } from '@contexts/ui.context'
import { Loop, translate } from '@utilities/helpers/core.helpers'
import { useViewport } from '@utilities/hooks/useViewport.hooks'

import { RaAccommodationGalleryModal } from './RaAccommodationGalleryModal'

import styles from './ra-accommodation-grid.module.scss'

const AccommodationLightboxCarousel = dynamic(() =>
  import(
    '@components/carousels/AccommodationLightboxCarousel/AccommodationLightboxCarousel'
  ).then(({ AccommodationLightboxCarousel }) => AccommodationLightboxCarousel)
)

export const RaAccommodationGridImage = ({
  gallery,
  image,
  isFirst,
  canExpandGallery,
  additionalImageCount,
  index,
  type,
}) => {
  const { showLightbox, showModal } = useUi()
  const { isMobile, isTablet } = useViewport()

  const slides = Loop(gallery, (item, i) => (
    <div className={styles['lightbox-slide']}>
      <RaImage
        key={i}
        image={item}
      />
      <div
        className={clsx(
          styles['lightbox-slide__captions'],
          item.caption && styles['lightbox-slide__captions--space-between']
        )}>
        {item.caption && (
          <RaTypography
            component="span"
            color="light"
            fontFamily="heading">
            {item.caption}
          </RaTypography>
        )}
        <RaTypography
          component="span"
          fontFamily="heading"
          color="light">
          {i + 1} / {gallery.length}
        </RaTypography>
      </div>
    </div>
  ))

  const isLastItem = useMemo(() => {
    if (!isTablet) return canExpandGallery
    if (isTablet && index == 2 && gallery.length > 3) return true
    return false
  }, [isMobile, isTablet, canExpandGallery])

  const badgeLabel = useMemo(() => {
    if (!isTablet) return additionalImageCount
    if (isTablet) return gallery.length - 3
  }, [isMobile, isTablet, additionalImageCount])

  const renderLightboxGallery = () => {
    if (!isLastItem) {
      showLightbox(
        <AccommodationLightboxCarousel
          slides={slides}
          activeIndex={index}
        />
      )
    }
  }

  const renderModalGallery = () => {
    showModal({
      title: translate('ALL_PHOTOS'),
      content: <RaAccommodationGalleryModal gallery={gallery} />,
    })
  }

  return (
    <ErrorBoundary>
      <div
        className={clsx(
          styles['grid__item'],
          isFirst && styles['grid__item--first']
        )}
        onClick={renderLightboxGallery}
        tabIndex={!isLastItem ? 0 : -1}>
        <RaImage
          image={image}
          height={225}
          elevation="light"
          cover
        />
        {isLastItem && (
          <div className={styles['grid__item--see-more']}>
            <button
              type="button"
              className={styles['grid__item--see-more__chip']}
              onClick={renderModalGallery}>
              <RaTypography component="span">
                + {badgeLabel} {translate('PHOTOS')}
              </RaTypography>
            </button>
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}
